import {
  STANDARDS_EDITION_NOTE,
  CONTROL_PLAN_NOTE,
  AP_VERIFY_NOTE,
} from "@/lovable-demo/lib/warranty/standards";
import { DecisionHistory } from "@/lovable-demo/components/decision-history";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  FileCheck2,
  Lock,
  Paperclip,
  Printer,
} from "lucide-react";
import { useEffect, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { CarryFactorDialog } from "@/lovable-demo/components/carry-factor-dialog";
import { Field, Panel, RiskBadge, StatusChip, VerdictBadge } from "@/lovable-demo/components/bits";
import { C1000Chart, CostForecastChart } from "@/lovable-demo/components/charts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { exportPdf } from "@/lovable-demo/lib/warranty/export";
import {
  fmtCurrency,
  fmtDate,
  fmtNumber,
  isOverdue,
  missingEvidence,
  recordDelayCost,
  recordNpr,
  recordVerdict,
  REMAINING_EXPOSURE_TOOLTIP,
  rpnAfter,
  rpnBefore,
  workflowProgress,
} from "@/lovable-demo/lib/warranty/calc";
import { rpnDelta } from "@/lovable-demo/lib/warranty/aggregate";
import { useClaims, useRecord, useSupplier, useWarranty } from "@/lovable-demo/lib/warranty/store";
import { openRecommendations, stageRecommendations } from "@/lovable-demo/lib/warranty/recommend";
import { RecommendationCard } from "@/lovable-demo/components/recommendation";
import { AttachEvidenceButton, EvidenceMeta } from "@/lovable-demo/components/evidence-dialog";
import { RecordEscalations } from "@/lovable-demo/components/escalations";
import { ClosureReview } from "@/lovable-demo/components/closure-review";

/** Problem-management clock: the next open workflow stage and its due date (distinct from the decision clock). */
function problemClockLabel(r: { stages: { name: string; status: string; dueDate?: string }[] }) {
  const next = r.stages.find((s) => s.status !== "complete");
  if (!next) return "All workflow stages complete";
  if (!next.dueDate) return `${next.name} · no due date`;
  const overdue = new Date(next.dueDate).getTime() < Date.now();
  return `${next.name} due ${new Date(next.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}${overdue ? " · overdue" : ""}`;
}
import { countsTowardGate } from "@/lovable-demo/lib/warranty/evidence-attach";
import { useDrafts } from "@/lovable-demo/lib/warranty/drafts";
import {
  dGate,
  dueConflict,
  evidenceFor,
  findInconsistencies,
  linkedDSteps,
  readyToClose,
  stageForD,
  stageGate,
  validateDDueDate,
} from "@/lovable-demo/lib/warranty/stage-link";
import {
  PfmeaRatingSelect,
  ApChange,
  PfmeaSignoffHistory,
  SignoffActions,
  SignoffSlots,
} from "@/lovable-demo/components/pfmea-change-dialog";
import { usePfmeaSignoffs, useMyIdentity } from "@/lovable-demo/lib/warranty/use-pfmea-signoffs";
import {
  awaitingMe,
  holdsSignoffRole,
  openRequestFor,
  type PfmeaChangeRequest,
  type SignoffRole,
} from "@/lovable-demo/lib/warranty/pfmea-signoff";
import { PFMEA_FIELD_LABEL } from "@/lovable-demo/lib/warranty/pfmea-governance";
import {
  AP_NOTE,
  apAfter,
  apBefore,
  recordApDelta,
} from "@/lovable-demo/lib/warranty/action-priority";
import {
  ApBadge,
  ChangeVerdictButton,
  useVerdictHistory,
  VerdictAuthorship,
  VerdictHistory,
} from "@/lovable-demo/components/compliance-verdict";
import {
  ClaimTrendPanel,
  CostBasis,
  ExpandableClaimRow,
  GovernedContainment,
  LegacyContainmentHistory,
  OriginStrip,
  ProblemStatementChapter,
  SignalsOriginChapter,
} from "@/lovable-demo/components/record-extensions";
import { CARRY_FACTOR_TOOLTIP } from "@/lovable-demo/lib/warranty/carry-factor";
import type { EightDStage, ProblemRecord, WorkflowStage } from "@/lovable-demo/lib/warranty/types";

export const Route = createFileRoute("/demo/problem-record/$id")({
  head: () => ({
    meta: [
      { title: "Governed Problem Record — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "An evidence-gated Problem Record with 8D, PFMEA, control plan, clause compliance, read-across and financial exposure as unified chapters.",
      },
      { property: "og:title", content: "Governed Problem Record — Broadview" },
      {
        property: "og:description",
        content:
          "An evidence-gated Problem Record with 8D, PFMEA, control plan, clause compliance, read-across and financial exposure as unified chapters.",
      },
    ],
  }),
  component: RecordDetail,
});

const scrollToId = (elId: string) =>
  window.setTimeout(() => {
    const el = document.getElementById(elId);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.classList.add("ring-2", "ring-primary");
    window.setTimeout(() => el?.classList.remove("ring-2", "ring-primary"), 1600);
  }, 80);

function RecordDetail() {
  const { id } = Route.useParams();
  const record = useRecord(id);
  const { data } = useWarranty();
  const [tab, setTab] = useState("statement");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [openStage, setOpenStage] = useState<string | null>(
    () => record?.stages.find((s) => s.status !== "complete")?.id ?? null,
  );
  useEffect(() => {
    try {
      setDetailsOpen(window.sessionStorage.getItem("broadview.record-details-open") === "true");
    } catch {
      /* session storage unavailable */
    }
  }, []);
  const jumpToD = (dId: string) => {
    setTab("8d");
    scrollToId(`eightd-${dId}`);
  };
  const jumpToStage = (stageId: string) => {
    setOpenStage(stageId);
    scrollToId(`stage-${stageId}`);
  };

  if (!record) {
    return (
      <AppShell title="Problem Record not found">
        <Panel>
          <p className="text-sm text-muted-foreground">
            No governed record matches <span className="num">{id}</span>.
          </p>
          <Link
            to="/demo/problem-records"
            className="mt-3 inline-block text-sm text-primary hover:underline"
          >
            Back to the registry
          </Link>
        </Panel>
      </AppShell>
    );
  }

  const program = data.programs.find((p) => p.id === record.programId);
  const progress = workflowProgress(record.stages);
  const setRememberedDetails = (open: boolean) => {
    setDetailsOpen(open);
    try {
      window.sessionStorage.setItem("broadview.record-details-open", String(open));
    } catch {
      /* session storage unavailable */
    }
  };

  return (
    <AppShell
      title={`${record.code} — ${record.title}`}
      subtitle={`${program?.name ?? record.platform} · ${record.component} · owner ${record.owner}`}
      actions={
        <Button variant="outline" size="sm" onClick={exportPdf}>
          <Printer className="size-3.5" /> PDF
        </Button>
      }
    >
      <div className="problem-record-print grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <Panel>
            <div className="flex flex-wrap items-center gap-2">
              <RiskBadge risk={record.risk} score={record.riskScore} />
              <StatusChip status={record.status} />
              <Field label="Remaining exposure" value={fmtCurrency(recordNpr(record))} />
              <Field label="Owner / champion" value={`${record.owner} / ${record.champion}`} />
              <Field
                label="Target close"
                value={
                  <span
                    className={
                      isOverdue(record.targetCloseAt, record.status)
                        ? "text-destructive"
                        : undefined
                    }
                  >
                    {fmtDate(record.targetCloseAt)}
                    {isOverdue(record.targetCloseAt, record.status) ? " · overdue" : ""}
                  </span>
                }
              />
              <DecisionCountControl record={record} />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded bg-elevated">
                <div className="h-full bg-primary" style={{ width: `${progress.pct}%` }} />
              </div>
              <span className="num text-xs text-muted-foreground">
                {progress.done}/{progress.total} stages complete
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="no-print mt-3 h-7 px-0 text-xs font-semibold text-primary hover:bg-transparent hover:underline"
              aria-expanded={detailsOpen}
              onClick={() => setRememberedDetails(!detailsOpen)}
            >
              {detailsOpen ? "Hide details" : "Details"}
            </Button>
            <div className={cn("record-details-print mt-3", !detailsOpen && "hidden")}>
              <p className="text-sm leading-relaxed text-foreground/90">{record.description}</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <Field label="Failure cause" value={record.failureCause} />
                <Field label="Supplier" value={record.supplier} />
                <Field label="Plant / source" value={record.plant} />
                <Field label="Claims" value={fmtNumber(record.claims)} />
                <Field label="Units in service" value={fmtNumber(record.unitsInService)} />
                <Field label="Total exposure" value={fmtCurrency(record.financial.exposure)} />
                <Field label="Opened" value={fmtDate(record.openedAt)} />
              </div>
              <OriginStrip record={record} />
            </div>
          </Panel>

          <div id="decision-queue">
            <DecisionQueue record={record} />
          </div>

          <div id="escalations">
            <RecordEscalations
              recordId={record.id}
              recordCode={record.code}
              problemClock={problemClockLabel(record)}
            />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto flex-wrap justify-start">
              <TabsTrigger value="statement">Problem Statement</TabsTrigger>
              <TabsTrigger value="signals">Signals &amp; Origin</TabsTrigger>
              <TabsTrigger value="8d">8D Chapter</TabsTrigger>
              <TabsTrigger value="pfmea">PFMEA / Control Plan</TabsTrigger>
              <TabsTrigger value="compliance">Standard Compliance</TabsTrigger>
              <TabsTrigger value="readacross">Read-Across</TabsTrigger>
              <TabsTrigger value="financial">Financial Exposure</TabsTrigger>
              <TabsTrigger value="containment">Containment &amp; Recovery</TabsTrigger>
              <TabsTrigger value="claims">Claims</TabsTrigger>
              <TabsTrigger value="decisions">Decision History</TabsTrigger>
            </TabsList>

            <TabsContent value="8d" className="mt-4">
              <EightDChapter record={record} onJumpToStage={jumpToStage} />
            </TabsContent>
            <TabsContent value="pfmea" className="mt-4">
              <PfmeaChapter record={record} />
            </TabsContent>
            <TabsContent value="compliance" className="mt-4">
              <ComplianceChapter record={record} />
            </TabsContent>
            <TabsContent value="readacross" className="mt-4">
              <ReadAcrossChapter record={record} />
            </TabsContent>
            <TabsContent value="financial" className="mt-4">
              <FinancialChapter record={record} />
              <CostBasis record={record} />
            </TabsContent>
            <TabsContent value="containment" className="mt-4">
              <GovernedContainment record={record} />
              <ContainmentChapter record={record} />
            </TabsContent>
            <TabsContent value="claims" className="mt-4">
              <ClaimsChapter record={record} />
              <ClaimTrendPanel record={record} />
            </TabsContent>
            <TabsContent value="statement" className="mt-4">
              <ProblemStatementChapter record={record} />
            </TabsContent>
            <TabsContent value="decisions" className="mt-4">
              <DecisionHistory record={record} />
            </TabsContent>
            <TabsContent value="signals" className="mt-4">
              <SignalsOriginChapter record={record} />
            </TabsContent>
          </Tabs>

          <Panel
            title="Append-only audit trail"
            subtitle="Every entry mapped to an IATF / ISO clause"
          >
            <ol className="space-y-3">
              {[...record.audit].reverse().map((a) => (
                <li key={a.id} className="flex gap-3 border-b border-border/60 pb-3 last:border-0">
                  <FileCheck2 className="mt-0.5 size-4 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{a.action}</p>
                    <p className="text-xs text-muted-foreground">{a.detail}</p>
                    <p className="num mt-1 text-[0.68rem] text-muted-foreground">
                      {fmtDate(a.at)} · {a.actor} · {a.clause}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        </div>

        <WorkflowRail
          record={record}
          openStage={openStage}
          setOpenStage={setOpenStage}
          onJumpToD={jumpToD}
        />
      </div>
    </AppShell>
  );
}

/* ---------------- decision queue ---------------- */

function useDecisionCounts(record: ProblemRecord) {
  const { data } = useWarranty();
  const claims = useClaims(record.id);
  const supplier = useSupplier(record.supplier);
  const program = data.programs.find((p) => p.id === record.programId);
  const recommendations = openRecommendations(record, claims, supplier, program);
  const { requests, signoffs } = usePfmeaSignoffs(record.id);
  const me = useMyIdentity();
  const pfmea = awaitingMe({ userId: me.userId, roles: me.roles, requests, signoffs }).length;
  const compliance = me.roles.includes("quality_director")
    ? record.clauses.filter((clause) => clause.verdict === "Violated").length
    : 0;
  return { recommendations, pfmea, compliance, total: recommendations.length + pfmea + compliance };
}

function DecisionCountControl({ record }: { record: ProblemRecord }) {
  const counts = useDecisionCounts(record);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="decision-count-control h-8"
      onClick={() => scrollToId("decision-queue")}
    >
      Needs your decision · {counts.total}
    </Button>
  );
}

function DecisionQueue({ record }: { record: ProblemRecord }) {
  const counts = useDecisionCounts(record);
  const open = counts.recommendations;
  const decided = record.decisions ?? [];

  return (
    <Panel
      title="Recommended decisions — human in the loop"
      subtitle={
        open.length > 0
          ? `${open.length} recommendation(s) awaiting a decision · ${decided.length} already decided`
          : `All current recommendations decided · ${decided.length} logged in the audit trail`
      }
    >
      <PfmeaSignoffQueue record={record} />
      {counts.compliance > 0 && (
        <div className="mb-3 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs">
          <p className="font-semibold text-destructive">
            Compliance items needing Director authority · {counts.compliance}
          </p>
          <p className="mt-1 text-muted-foreground">
            Open Standard Compliance to review the current Violated verdicts.
          </p>
        </div>
      )}
      {open.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing is waiting on you. New recommendations appear as claims, evidence and stage
          progress change.
        </p>
      ) : (
        <div className="space-y-2">
          {open.slice(0, 4).map((rec) => (
            <div key={rec.id}>
              <p className="num mb-1 text-[0.64rem] uppercase tracking-wider text-muted-foreground">
                Stage {rec.stageIndex} · {record.stages.find((s) => s.id === rec.stageId)?.name}
              </p>
              <RecommendationCard record={record} rec={rec} />
            </div>
          ))}
          {open.length > 4 && (
            <p className="text-xs text-muted-foreground">
              {open.length - 4} further recommendation(s) sit on later stages in the workflow rail.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

/* ---------------- workflow rail ---------------- */

function WorkflowRail({
  record,
  openStage,
  setOpenStage,
  onJumpToD,
}: {
  record: ProblemRecord;
  openStage: string | null;
  setOpenStage: (id: string | null) => void;
  onJumpToD: (dId: string) => void;
}) {
  const { completeStage, addStageEvidence, addStageNote, data, eightDMapping } = useWarranty();
  const claims = useClaims(record.id);
  const supplier = useSupplier(record.supplier);
  const program = data.programs.find((p) => p.id === record.programId);
  // Per-stage note drafts, keyed by stage id — text never moves between stages.
  const noteDrafts = useDrafts();
  const flags = findInconsistencies(record, eightDMapping);

  return (
    <Panel
      title="Operational workflow"
      subtitle="11 stages · evidence-gated · linked to the 8D chapter · every stage carries a recommendation for human decision"
    >
      <ol className="space-y-1.5">
        {record.stages.map((stage) => {
          const open = openStage === stage.id;
          const gate = stageGate(record, stage, eightDMapping);
          const missing = gate.missing;
          const gated = gate.blocked;
          const linkedD = linkedDSteps(record, stage, eightDMapping);
          const stageFlags = flags.filter(
            (f) =>
              f.target === stage.id ||
              (linkedD.some((x) => x.id === f.target) && f.explanation.includes("after Stage")),
          );
          const overdue = isOverdue(stage.dueDate, stage.status);
          const decisions = record.decisions ?? [];
          const recs =
            stage.status === "complete"
              ? []
              : stageRecommendations(record, stage, claims, supplier, program);
          const pending = recs.filter(
            (r) => !decisions.some((d) => d.recommendationId === r.id),
          ).length;
          return (
            <li
              id={`stage-${stage.id}`}
              data-testid={`demo-stage-${stage.index}`}
              key={stage.id}
              className="rounded border border-border/70 bg-elevated/40"
            >
              <button
                onClick={() => setOpenStage(open ? null : stage.id)}
                className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left"
              >
                <StageIcon stage={stage} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium leading-tight">
                    <span className="num text-muted-foreground">{stage.index}. </span>
                    {stage.name}
                  </span>
                  <span className="num mt-0.5 block text-[0.68rem] text-muted-foreground">
                    {stage.owner} · due {fmtDate(stage.dueDate)}
                  </span>
                  {stage.status !== "complete" && gate.openD.length > 0 && (
                    <span className="mt-0.5 block text-[0.66rem] text-warning">
                      Waiting on {gate.openD.join(", ")}
                    </span>
                  )}
                </span>
                {stageFlags.length > 0 && (
                  <span className="rounded border border-warning/50 bg-warning/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-warning">
                    inconsistent
                  </span>
                )}
                {linkedD.map((x) => (
                  <span
                    key={x.id}
                    className={cn(
                      "num rounded border px-1.5 py-0.5 text-[0.6rem] font-semibold",
                      x.status === "complete"
                        ? "border-success/40 bg-success/15 text-success"
                        : "border-border bg-muted text-muted-foreground",
                    )}
                  >
                    {x.id}
                  </span>
                ))}
                {overdue && (
                  <span className="rounded border border-destructive/40 bg-destructive/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-destructive">
                    overdue
                  </span>
                )}
                {pending > 0 && (
                  <span className="num rounded border border-primary/40 bg-primary/12 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-primary">
                    {pending} rec
                  </span>
                )}
                <ChevronRight
                  className={cn(
                    "mt-0.5 size-4 text-muted-foreground transition-transform",
                    open && "rotate-90",
                  )}
                />
              </button>

              {open && (
                <div className="border-t border-border/70 px-3 py-3">
                  <p className="text-xs leading-relaxed text-muted-foreground">{stage.purpose}</p>
                  <p className="mt-2 text-[0.68rem] text-primary">{stage.clause}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {linkedD.length === 0 ? (
                      <span className="text-[0.66rem] text-muted-foreground">
                        No 8D step — {stage.index <= 3 ? "pre-8D triage" : "workflow-only stage"}
                      </span>
                    ) : (
                      <>
                        <span className="text-[0.66rem] text-muted-foreground">Linked 8D:</span>
                        {linkedD.map((x) => (
                          <LinkChip
                            key={x.id}
                            label={x.id}
                            done={x.status === "complete"}
                            onClick={() => onJumpToD(x.id)}
                          />
                        ))}
                      </>
                    )}
                  </div>
                  {stageFlags.map((f, i) => (
                    <InconsistentFlag key={i} text={f.explanation} />
                  ))}
                  {linkedD
                    .filter((x) => dueConflict(x, stage))
                    .map((x) => (
                      <p
                        key={x.id}
                        className="mt-1 flex items-start gap-1.5 text-[0.68rem] text-warning"
                      >
                        <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                        {x.id} is due {fmtDate(x.dueDate)}, after this stage's due date.
                      </p>
                    ))}

                  {recs.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {recs.map((rec) => (
                        <RecommendationCard
                          key={rec.id}
                          record={record}
                          rec={rec}
                          decision={decisions.find((d) => d.recommendationId === rec.id)}
                        />
                      ))}
                    </div>
                  )}

                  <p className="mt-3 text-[0.66rem] font-semibold uppercase tracking-wider text-muted-foreground">
                    Required evidence
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {stage.requiredEvidence.map((req) => {
                      const hit = evidenceFor(
                        record,
                        { kind: "stage", id: stage.id },
                        req,
                        eightDMapping,
                      );
                      const attached = hit.own ?? hit.shared?.item;
                      const shown = attached ?? stage.evidence.find((e) => e.name === req);
                      return (
                        <li
                          key={req}
                          data-demo-evidence={req}
                          className="flex items-start gap-2 text-xs"
                        >
                          {attached ? (
                            <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success" />
                          ) : (
                            <Circle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                          )}
                          <span className="min-w-0 flex-1">
                            <span
                              className={attached ? "text-foreground" : "text-muted-foreground"}
                            >
                              {req}
                            </span>
                            {hit.shared && <SharedNote from={hit.shared.from} />}
                            {shown && <EvidenceMeta item={shown} />}
                          </span>
                          {!attached && stage.status !== "complete" && (
                            <AttachEvidenceButton
                              recordId={record.id}
                              gateId={stage.id}
                              requirement={req}
                              onAttach={(attachment) =>
                                addStageEvidence(record.id, stage.id, req, attachment)
                              }
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {stage.notes.length > 0 && (
                    <ul className="mt-3 space-y-1.5 border-t border-border/60 pt-3">
                      {stage.notes.map((n) => (
                        <li key={n.id} className="text-xs text-muted-foreground">
                          <span className="text-foreground">{n.text}</span>
                          <span className="num block text-[0.64rem]">
                            {n.author} · {fmtDate(n.at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {stage.status !== "complete" && (
                    <div className="mt-3 space-y-2">
                      {stage.index === 11 && <ClosureReview record={record} />}
                      <Textarea
                        value={noteDrafts.get(stage.id)}
                        onChange={(e) => noteDrafts.set(stage.id, e.target.value)}
                        placeholder="Add a governance note…"
                        className="min-h-16 bg-card text-xs"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={!noteDrafts.get(stage.id).trim()}
                          onClick={async () => {
                            const stageId = stage.id;
                            const text = noteDrafts.get(stageId).trim();
                            const result = await addStageNote(record.id, stageId, text);
                            if (result.ok) {
                              noteDrafts.clear(stageId);
                              toast.success("Note confirmed in the audit trail");
                            } else {
                              toast.error("Note not recorded", { description: result.error });
                            }
                          }}
                        >
                          Add note
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={gated}
                          onClick={async () => {
                            const result = await completeStage(record.id, stage.id);
                            if (result.ok)
                              toast.success(`Stage ${stage.index} closed`, {
                                description: stage.name,
                              });
                            else toast.error("Stage not closed", { description: result.error });
                          }}
                        >
                          {gated ? (
                            <Lock className="size-3" />
                          ) : (
                            <CheckCircle2 className="size-3" />
                          )}
                          Complete stage
                        </Button>
                      </div>
                      {gated && (
                        <p className="flex items-start gap-1.5 text-[0.68rem] text-warning">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                          {gate.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

function StageIcon({ stage }: { stage: WorkflowStage }) {
  if (stage.status === "complete")
    return <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />;
  if (stage.status === "in-progress")
    return <Clock className="mt-0.5 size-4 shrink-0 text-warning" />;
  return <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />;
}

/* ---------------- 8D ---------------- */

function LinkChip({ label, done, onClick }: { label: string; done: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Go to ${label}`}
      className={cn(
        "num rounded border px-1.5 py-0.5 text-[0.62rem] font-semibold transition-colors hover:border-primary",
        done
          ? "border-success/40 bg-success/15 text-success"
          : "border-primary/40 bg-primary/12 text-primary",
      )}
    >
      {label}
    </button>
  );
}

function SharedNote({ from }: { from: string }) {
  return (
    <span className="block text-[0.64rem] text-primary">
      Shared evidence — attached once under {from}, counts here too
    </span>
  );
}

function InconsistentFlag({ text }: { text: string }) {
  return (
    <p className="mt-2 flex items-start gap-1.5 rounded border border-warning/40 bg-warning/10 px-2 py-1 text-[0.68rem] text-warning">
      <AlertTriangle className="mt-0.5 size-3 shrink-0" />
      <span>
        <span className="font-semibold">Inconsistent — recorded before linking.</span> {text}{" "}
        Nothing has been changed; review and correct through the normal governed steps.
      </span>
    </p>
  );
}

function DueDateEditor({ record, d }: { record: ProblemRecord; d: EightDStage }) {
  const { setEightDDueDate, eightDMapping } = useWarranty();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(d.dueDate.slice(0, 10));
  const error = validateDDueDate(record, d.id, value, eightDMapping);
  if (!editing)
    return (
      <button
        type="button"
        className="text-[0.66rem] text-primary hover:underline"
        onClick={() => setEditing(true)}
      >
        Change due date
      </button>
    );
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <input
        type="date"
        aria-label={`${d.id} due date`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-7 rounded border border-border bg-card px-2 text-xs"
      />
      <Button
        size="sm"
        className="h-7 text-xs"
        disabled={!!error}
        onClick={async () => {
          const r = await setEightDDueDate(record.id, d.id, value);
          if (r.ok) {
            toast.success(`${d.id} due date confirmed in the audit trail`);
            setEditing(false);
          } else toast.error("Due date not saved", { description: r.error });
        }}
      >
        Save
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {error && <span className="w-full text-[0.66rem] text-warning">{error}</span>}
    </div>
  );
}

function EightDChapter({
  record,
  onJumpToStage,
}: {
  record: ProblemRecord;
  onJumpToStage: (stageId: string) => void;
}) {
  const { addEightDEvidence, completeEightD, completeStage, eightDMapping } = useWarranty();
  const flags = findInconsistencies(record, eightDMapping);
  return (
    <Panel
      title="8D Chapter"
      subtitle="D1–D8 live inside the governed record and are linked to the workflow stages"
    >
      <ol className="space-y-2">
        {record.eightD.map((d: EightDStage) => {
          const gate = dGate(record, d, eightDMapping);
          const stage = stageForD(record, d.id, eightDMapping);
          const overdue = isOverdue(d.dueDate, d.status);
          const conflict = dueConflict(d, stage);
          const dFlags = flags.filter(
            (f) =>
              f.target === d.id || (stage && f.target === stage.id && f.explanation.includes(d.id)),
          );
          const prompt =
            stage &&
            d.status === "complete" &&
            readyToClose(record, stage, eightDMapping) &&
            linkedDSteps(record, stage, eightDMapping).at(-1)?.id === d.id;
          return (
            <li
              id={`eightd-${d.id}`}
              data-testid={`demo-eightd-${d.id}`}
              key={d.id}
              className="rounded border border-border/70 bg-elevated/40 p-3"
            >
              <div className="flex flex-wrap items-start gap-3">
                <span
                  className={cn(
                    "num grid size-8 shrink-0 place-items-center rounded text-sm font-semibold",
                    d.status === "complete"
                      ? "bg-success/15 text-success"
                      : d.status === "in-progress"
                        ? "bg-warning/15 text-warning"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {d.id}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {d.title}
                    {stage && (
                      <LinkChip
                        label={`Stage ${stage.index}`}
                        done={stage.status === "complete"}
                        onClick={() => onJumpToStage(stage.id)}
                      />
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">{d.objective}</p>
                  <p className="num mt-1 text-[0.68rem] text-muted-foreground">
                    {d.owner} · due {fmtDate(d.dueDate)}
                    {stage && <> · stage due {fmtDate(stage.dueDate)}</>}
                  </p>
                  {d.status !== "complete" && <DueDateEditor record={record} d={d} />}
                </div>
                {overdue && (
                  <span className="rounded border border-destructive/40 bg-destructive/15 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-destructive">
                    overdue
                  </span>
                )}
                <VerdictBadge
                  verdict={
                    d.status === "complete"
                      ? "Complete"
                      : d.status === "in-progress"
                        ? "In progress"
                        : "Not started"
                  }
                />
              </div>

              {conflict && stage && (
                <p className="mt-2 flex items-start gap-1.5 text-[0.68rem] text-warning">
                  <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                  {d.id} is due after Stage {stage.index} ({fmtDate(stage.dueDate)}). A D step may
                  not be due later than its stage.
                </p>
              )}
              {dFlags.map((f, i) => (
                <InconsistentFlag key={i} text={f.explanation} />
              ))}

              <ul className="mt-3 space-y-1 border-t border-border/60 pt-2">
                {d.requiredEvidence.map((req) => {
                  const hit = evidenceFor(record, { kind: "d", id: d.id }, req, eightDMapping);
                  const attached = hit.own ?? hit.shared?.item;
                  const shown = attached ?? d.evidence.find((e) => e.name === req);
                  return (
                    <li
                      key={req}
                      data-demo-evidence={req}
                      className="flex items-start gap-2 text-xs"
                    >
                      {attached ? (
                        <CheckCircle2 className="size-3.5 shrink-0 text-success" />
                      ) : (
                        <Circle className="size-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{req}</span>
                        {hit.shared && <SharedNote from={hit.shared.from} />}
                        {shown && <EvidenceMeta item={shown} />}
                      </span>
                      {!attached && d.status !== "complete" && (
                        <AttachEvidenceButton
                          recordId={record.id}
                          gateId={d.id}
                          requirement={req}
                          onAttach={(attachment) =>
                            addEightDEvidence(record.id, d.id, req, attachment)
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ul>

              {d.notes.map((n) => (
                <p key={n.id} className="mt-2 text-xs text-muted-foreground">
                  {n.text}
                </p>
              ))}

              {d.status !== "complete" && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={gate.blocked}
                    onClick={async () => {
                      const result = await completeEightD(record.id, d.id);
                      if (result.ok) toast.success(`${d.id} closed`);
                      else toast.error(`${d.id} not closed`, { description: result.error });
                    }}
                  >
                    {gate.blocked ? (
                      <Lock className="size-3" />
                    ) : (
                      <CheckCircle2 className="size-3" />
                    )}
                    Complete {d.id}
                  </Button>
                  {gate.message && (
                    <span className="text-[0.68rem] text-warning">{gate.message}</span>
                  )}
                </div>
              )}

              {prompt && stage && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-success/40 bg-success/10 px-2 py-1.5">
                  <span className="text-xs text-success">
                    {d.id} complete — Stage {stage.index} is ready to close. Complete Stage{" "}
                    {stage.index} now?
                  </span>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={async () => {
                      const r = await completeStage(record.id, stage.id);
                      if (r.ok)
                        toast.success(`Stage ${stage.index} closed`, { description: stage.name });
                      else toast.error("Stage not closed", { description: r.error });
                    }}
                  >
                    Complete Stage {stage.index}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => onJumpToStage(stage.id)}
                  >
                    Review stage
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

/* ---------------- PFMEA ---------------- */

/** Shared sign-off wiring for the PFMEA chapter and the Decision Queue. */
function usePfmeaSignoffActions(record: ProblemRecord) {
  const { proposePfmeaChange, signOffPfmea } = useWarranty();
  const { requests, signoffs, refresh } = usePfmeaSignoffs(record.id);
  const me = useMyIdentity();
  const onSignOff = async (
    request: PfmeaChangeRequest,
    decision: "approve" | "reject",
    role: SignoffRole,
    comment: string,
  ) => {
    const r = await signOffPfmea(record.id, request, signoffs, decision, role, comment);
    await refresh();
    return r;
  };
  const propose = async (
    rowId: string,
    field: PfmeaChangeRequest["field"],
    value: number,
    rationale: string,
  ) => {
    const r = await proposePfmeaChange(
      record.id,
      rowId,
      field,
      value,
      rationale,
      openRequestFor(requests, signoffs, rowId, field),
    );
    await refresh();
    return r;
  };
  return { requests, signoffs, me, onSignOff, propose };
}

function PfmeaSignoffQueue({ record }: { record: ProblemRecord }) {
  const { requests, signoffs, me, onSignOff } = usePfmeaSignoffActions(record);
  const approver = (["quality", "manufacturing", "director"] as const).some((r) =>
    holdsSignoffRole(me.roles, r),
  );
  if (!approver) return null;
  const mine = awaitingMe({ userId: me.userId, roles: me.roles, requests, signoffs });
  return (
    <div className="mb-3 rounded border border-warning/30 bg-warning/5 p-3">
      <p className="text-[0.66rem] font-semibold uppercase tracking-wider text-warning">
        PFMEA sign-offs awaiting me · {mine.length}
      </p>
      {mine.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          No PFMEA rating changes need your sign-off.
        </p>
      ) : (
        <div className="mt-2 space-y-2">
          {mine.map((req) => {
            const row = record.pfmea.find((r) => r.id === req.rowId);
            return (
              <div
                key={req.id}
                className="rounded border border-border/70 bg-card/60 p-2.5 text-xs"
              >
                <p className="text-foreground">
                  {row?.failureMode} — {PFMEA_FIELD_LABEL[req.field]}{" "}
                  <span className="num">
                    {req.oldValue} → {req.newValue}
                  </span>{" "}
                  <ApChange row={row} req={req} />{" "}
                  <span className="num text-muted-foreground">
                    (RPN legacy {req.rpnBefore} → {req.rpnAfter})
                  </span>
                </p>
                <p className="text-muted-foreground">
                  Proposed by {req.proposedByLabel} — “{req.rationale}”
                </p>
                <SignoffSlots request={req} signoffs={signoffs} />
                <SignoffActions request={req} signoffs={signoffs} me={me} onSignOff={onSignOff} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PfmeaChapter({ record }: { record: ProblemRecord }) {
  const { requests, signoffs, me, onSignOff, propose } = usePfmeaSignoffActions(record);
  const delta = rpnDelta(record);
  const ap = recordApDelta(record.pfmea);
  return (
    <div className="space-y-4">
      <Panel
        title="PFMEA ratings"
        subtitle={`Highest AP: ${ap.before} → ${ap.after} · RPN (legacy) ${delta.before} → ${delta.after} (${delta.reduction}% reduction)`}
      >
        <p className="mb-3 text-xs text-muted-foreground">{AP_NOTE}</p>
        <p className="mb-3 text-xs text-warning">{AP_VERIFY_NOTE}</p>
        <div className="space-y-4">
          {record.pfmea.map((row) => (
            <div key={row.id} className="rounded border border-border/70 bg-elevated/40 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Process step" value={row.process} />
                <Field label="Failure mode" value={row.failureMode} />
                <Field label="Effect" value={row.effect} />
                <Field label="Cause" value={row.cause} />
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="py-1.5 text-left font-medium">State</th>
                      <th className="py-1.5 text-center font-medium">Sev</th>
                      <th className="py-1.5 text-center font-medium">Occ</th>
                      <th className="py-1.5 text-center font-medium">Det</th>
                      <th className="py-1.5 text-center font-medium">AP</th>
                      <th className="py-1.5 text-right font-medium">RPN (legacy)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    <tr>
                      <td className="py-2">Before action</td>
                      <td className="num py-2 text-center">{row.sevBefore}</td>
                      <td className="num py-2 text-center">{row.occBefore}</td>
                      <td className="num py-2 text-center">{row.detBefore}</td>
                      <td className="py-2 text-center" data-testid="ap-before">
                        <ApBadge ap={apBefore(row)} />
                      </td>
                      <td className="num py-2 text-right text-muted-foreground">
                        {rpnBefore(row)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2">After action</td>
                      {(["sevAfter", "occAfter", "detAfter"] as const).map((k) => (
                        <td key={k} className="py-2 text-center">
                          <PfmeaRatingSelect
                            row={row}
                            field={k}
                            openRequest={openRequestFor(requests, signoffs, row.id, k)}
                            onConfirm={(value, rationale) => propose(row.id, k, value, rationale)}
                          />
                        </td>
                      ))}
                      <td className="py-2 text-center" data-testid="ap-after">
                        <ApBadge ap={apAfter(row)} />
                      </td>
                      <td className="num py-2 text-right text-muted-foreground">{rpnAfter(row)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <PfmeaSignoffHistory
                row={row}
                requests={requests}
                signoffs={signoffs}
                me={me}
                onSignOff={onSignOff}
              />

              <div className="mt-3 rounded border border-warning/30 bg-warning/8 p-2.5">
                <p className="text-[0.66rem] font-semibold uppercase tracking-wider text-warning">
                  Missed-rating rationale
                </p>
                <p className="mt-1 text-xs text-foreground/90">{row.missedRatingRationale}</p>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel
        title="Control plan"
        subtitle="Techniques, frequency and reaction plans linked to each failure mode"
      >
        <p className="mb-3 text-xs text-muted-foreground">{CONTROL_PLAN_NOTE}</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-2 py-2 text-left font-medium">Process / failure mode</th>
                <th className="px-2 py-2 text-left font-medium">Control technique</th>
                <th className="px-2 py-2 text-left font-medium">Method</th>
                <th className="px-2 py-2 text-left font-medium">Frequency</th>
                <th className="px-2 py-2 text-left font-medium">Reaction plan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {record.pfmea.map((row) => (
                <tr key={row.id}>
                  <td className="px-2 py-2.5">
                    <span className="block font-medium">{row.process}</span>
                    <span className="block text-xs text-muted-foreground">{row.failureMode}</span>
                  </td>
                  <td className="px-2 py-2.5 text-muted-foreground">{row.controlTechnique}</td>
                  <td className="px-2 py-2.5 text-muted-foreground">{row.controlMethod}</td>
                  <td className="px-2 py-2.5 text-muted-foreground">{row.frequency}</td>
                  <td className="px-2 py-2.5 text-muted-foreground">{row.reactionPlan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/* ---------------- compliance ---------------- */

function ComplianceChapter({ record }: { record: ProblemRecord }) {
  const { history, refresh } = useVerdictHistory(record.id);
  const counts = record.clauses.reduce<Record<string, number>>((acc, c) => {
    acc[c.verdict] = (acc[c.verdict] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <Panel
      title="Standard compliance"
      subtitle={`IATF 16949:2016 / ISO 9001 (2015 → 2026 transition) — ${counts["Violated"] ?? 0} violated, ${counts["Gap"] ?? 0} gaps, ${counts["Compliant"] ?? 0} compliant`}
    >
      <p className="mb-3 text-xs text-muted-foreground">{STANDARDS_EDITION_NOTE}</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Standard</th>
              <th className="px-2 py-2 text-left font-medium">Clause</th>
              <th className="px-2 py-2 text-left font-medium">Requirement</th>
              <th className="px-2 py-2 text-left font-medium">Linked stage</th>
              <th className="px-2 py-2 text-left font-medium">Verdict</th>
              <th className="px-2 py-2 text-left font-medium">Rationale</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {record.clauses.map((c) => (
              <tr key={c.id}>
                <td className="px-2 py-2.5 text-muted-foreground">{c.standard}</td>
                <td className="num px-2 py-2.5 font-medium text-primary">{c.clause}</td>
                <td className="px-2 py-2.5">{c.title}</td>
                <td className="px-2 py-2.5 text-muted-foreground">{c.linkedStage}</td>
                <td className="px-2 py-2.5">
                  <VerdictBadge verdict={c.verdict} />
                  <div className="mt-1">
                    <ChangeVerdictButton record={record} clause={c} onDone={() => void refresh()} />
                  </div>
                </td>
                <td className="px-2 py-2.5 text-xs text-muted-foreground">
                  {c.rationale}
                  <VerdictAuthorship clause={c} record={record} />
                  <VerdictHistory items={history.filter((h) => h.clauseId === c.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ---------------- read across ---------------- */

function ReadAcrossChapter({ record }: { record: ProblemRecord }) {
  return (
    <Panel title="Read-across" subtitle="Affected platforms and prevention actions with drilldown">
      <div className="space-y-3">
        {record.readAcross.map((ra) => (
          <div key={ra.id} className="rounded border border-border/70 bg-elevated/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{ra.program}</p>
                <p className="text-xs text-muted-foreground">
                  {ra.platform} · {ra.sharedComponent}
                </p>
              </div>
              <VerdictBadge verdict={ra.status} />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <Field label="Units in service" value={fmtNumber(ra.unitsInService)} />
              <Field label="Exposure" value={fmtCurrency(ra.exposure)} />
              <Field label="Owner" value={ra.owner} />
              <Field label="Prevention action" value={ra.preventionAction} />
            </div>
            <Link
              to="/demo/prevention"
              className="mt-3 inline-block text-xs text-primary hover:underline"
            >
              Open in prevention intelligence
            </Link>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------------- financial ---------------- */

function FinancialChapter({ record }: { record: ProblemRecord }) {
  const { changeRecordCarryFactor } = useWarranty();
  const f = record.financial;
  const npr = recordNpr(record);
  const delay = recordDelayCost(record);
  const verdict = recordVerdict(f);
  return (
    <div className="space-y-4">
      <Panel
        title="Financial exposure"
        subtitle="Actual, exposure, remaining exposure and delay cost"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Actual cost to date" value={fmtCurrency(f.actualCost)} />
          <Field label="Total exposure" value={fmtCurrency(f.exposure)} />
          <Field
            label={<span title={REMAINING_EXPOSURE_TOOLTIP}>Remaining exposure</span>}
            value={fmtCurrency(npr)}
          />
          <Field
            label={<span title={CARRY_FACTOR_TOOLTIP}>Delay cost / month</span>}
            value={
              <>
                {fmtCurrency(delay)}{" "}
                <span className="block text-[0.68rem] font-normal text-muted-foreground">
                  carry {(f.monthlyCarryFactor * 100).toFixed(1)}% ·{" "}
                  {f.monthlyCarryFactorSource === "per-record override"
                    ? "per-record override"
                    : "per-record value"}
                </span>
              </>
            }
          />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Field label="Target reduction" value={`${f.targetReductionPct}%`} />
          <Field label="Observed reduction" value={`${f.observedReductionPct}%`} />
          <Field label="Performance vs Target" value={<VerdictBadge verdict={verdict} />} />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Delay cost forecast, based on remaining exposure × monthly carry factor: each month of
          slippage carries <span className="num text-warning">{fmtCurrency(delay)}</span>, or{" "}
          <span className="num text-warning">{fmtCurrency(delay * 3)}</span> over a quarter.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <CarryFactorDialog
            current={f.monthlyCarryFactor}
            label="Change record carry factor"
            onConfirm={(value, rationale) => changeRecordCarryFactor(record.id, value, rationale)}
          />
          <span className="text-xs text-muted-foreground" title={CARRY_FACTOR_TOOLTIP}>
            Source:{" "}
            {f.monthlyCarryFactorSource === "per-record override"
              ? `${f.monthlyCarryFactorUpdatedBy ?? "Quality Director"}${f.monthlyCarryFactorUpdatedAt ? ` · ${fmtDate(f.monthlyCarryFactorUpdatedAt)}` : ""}`
              : "initial record value"}
          </span>
        </div>
      </Panel>
      <Panel
        title="Cost vs projected cost"
        subtitle="Month-over-month growth projection; not an AI or statistical model"
      >
        <CostForecastChart data={f.forecast} />
      </Panel>
      <Panel
        title="C/1000 for this failure mode"
        subtitle="Aggregate claims per 1,000 units in service"
      >
        <C1000Chart data={record.c1000} height={200} />
      </Panel>
    </div>
  );
}

/* ---------------- containment ---------------- */

function SupplierCard({ name }: { name: string }) {
  const supplier = useSupplier(name);
  if (!supplier) return null;
  return (
    <div className="rounded border border-border/70 bg-elevated/40 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-display text-sm font-semibold">{supplier.name}</p>
        <span className="text-[0.62rem] uppercase tracking-[0.14em] text-muted-foreground">
          {supplier.tier} · {supplier.category}
        </span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Quality contact"
          value={
            <>
              {supplier.contactName}
              <span className="block text-[0.68rem] font-normal text-muted-foreground">
                {supplier.contactTitle}
              </span>
            </>
          }
        />
        <Field
          label="Contact details"
          value={
            <>
              <a
                className="block break-all text-primary hover:underline"
                href={`mailto:${supplier.email}`}
              >
                {supplier.email}
              </a>
              <span className="num block text-[0.68rem] font-normal text-muted-foreground">
                {supplier.phone}
              </span>
            </>
          }
        />
        <Field
          label="Site"
          value={
            <>
              {supplier.address}
              <span className="block text-[0.68rem] font-normal text-muted-foreground">
                {supplier.city}, {supplier.country}
              </span>
            </>
          }
        />
        <Field
          label="Performance"
          value={
            <>
              <span className="num">{supplier.qualityRating.toFixed(1)}/5</span>
              <span className="num block text-[0.68rem] font-normal text-muted-foreground">
                {fmtNumber(supplier.ppm)} PPM · {supplier.contractRef}
              </span>
            </>
          }
        />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        SQE owner: {supplier.sqeOwner} · Escalation: {supplier.escalationContact}
      </p>
    </div>
  );
}

function ContainmentChapter({ record }: { record: ProblemRecord }) {
  const supplierNames = Array.from(
    new Set([record.supplier, ...record.recovery.map((r) => r.supplier)].filter(Boolean)),
  );
  return (
    <div className="space-y-4">
      <Panel
        title="Supplier directory"
        subtitle="Responsible suppliers for containment and recovery on this record"
      >
        <div className="space-y-3">
          {supplierNames.map((n) => (
            <SupplierCard key={n} name={n} />
          ))}
        </div>
      </Panel>
      <Panel
        title="Supplier recovery"
        subtitle={`${fmtCurrency(record.financial.supplierRecovered)} recovered of ${fmtCurrency(record.financial.supplierRecoveryTarget)} target`}
      >
        {record.recovery.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No recovery actions raised yet — responsibility is not established at this stage.
          </p>
        ) : (
          <ul className="space-y-2">
            {record.recovery.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center gap-3 rounded border border-border/70 bg-elevated/40 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{r.action}</p>
                  <p className="text-xs text-muted-foreground">{r.supplier}</p>
                </div>
                <span className="num text-sm font-semibold">{fmtCurrency(r.amount)}</span>
                <VerdictBadge verdict={r.status} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <LegacyContainmentHistory record={record} />
    </div>
  );
}

function ClaimsChapter({ record }: { record: ProblemRecord }) {
  const claims = useClaims(record.id);
  const total = claims.reduce((sum, c) => sum + c.totalCost, 0);

  return (
    <Panel
      title={`Linked warranty claims — ${claims.length}`}
      subtitle={`${fmtCurrency(total)} claimed cost · VIN, failure and repair dates from the claims database`}
    >
      {claims.length === 0 ? (
        <p className="text-sm text-muted-foreground">No claims linked to this record yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead className="text-[0.66rem] uppercase tracking-[0.14em] text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3">Claim</th>
                <th className="py-2 pr-3">VIN</th>
                <th className="py-2 pr-3">Failure</th>
                <th className="py-2 pr-3">Repair</th>
                <th className="py-2 pr-3">Mileage</th>
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 pr-3">Dealer</th>
                <th className="py-2 pr-3">Cost</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {[...claims]
                .sort((a, b) => b.repairDate.localeCompare(a.repairDate))
                .map((c) => (
                  <ExpandableClaimRow key={c.id} claim={c} colSpan={9}>
                    <td className="num py-2 pr-3 font-semibold">{c.claimNumber}</td>
                    <td className="num py-2 pr-3">{c.vin}</td>
                    <td className="num py-2 pr-3">{c.failureDate}</td>
                    <td className="num py-2 pr-3">{c.repairDate}</td>
                    <td className="num py-2 pr-3">{fmtNumber(c.mileage)}</td>
                    <td className="max-w-[22rem] py-2 pr-3 text-muted-foreground">
                      {c.description}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{c.dealer}</td>
                    <td className="num py-2 pr-3">{fmtCurrency(c.totalCost)}</td>
                    <td className="py-2 capitalize text-muted-foreground">{c.status}</td>
                  </ExpandableClaimRow>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
